"use client";

import {
  addDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { getV3Db } from "./db";
import {
  v3GamesCol,
  v3PeopleCol,
  v3OrganizationsCol,
  v3TeamsCol,
  v3LocationsCol,
  v3ExpensesCol,
  v3MileageTripsCol,
  v3VoiceNotesCol,
  v3ImportJobsCol,
  v3ExportsCol,
  v3Doc,
  v3UserDoc,
  v3SettingDoc,
  v3AddressesCol,
} from "./collections";
import { V3_COLLECTIONS } from "../types.v3";
import { computeGameStatus } from "./game-status";
import { computePayStatus } from "./pay-status";
import {
  validate,
  gameCreateSchema,
  personCreateSchema,
  organizationCreateSchema,
  expenseCreateSchema,
  locationCreateSchema,
} from "./validators";
import type {
  V3Game,
  V3CrewMember,
  V3Person,
  V3Organization,
  V3Team,
  V3Location,
  V3Expense,
  V3MileageTrip,
  V3TaxYearConfig,
  V3VoiceNote,
  V3ImportJob,
  V3ExportRecord,
  V3UserProfile,
} from "../types.v3";

type CreateInput<T> = Omit<T, "id" | "createdAt" | "updatedAt">;
type UpdateInput<T> = Partial<Omit<T, "id" | "createdAt">>;

function now() {
  return Timestamp.now();
}

/** Removes undefined values from an object (recursively). Firestore rejects undefined. */
export function stripUndefined<T extends Record<string, unknown>>(
  obj: T,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) {
      out[k] = v.map((item) =>
        item !== null && typeof item === "object" && !(item instanceof Timestamp)
          ? stripUndefined(item as Record<string, unknown>)
          : item,
      );
    } else if (
      v !== null &&
      typeof v === "object" &&
      !(v instanceof Timestamp) &&
      !(v instanceof Date)
    ) {
      out[k] = stripUndefined(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Location resolution (find-or-create by name)
// ---------------------------------------------------------------------------

export async function resolveLocationId(
  uid: string,
  locationName: string,
): Promise<string> {
  const q = query(
    v3LocationsCol(uid),
    where("name", "==", locationName),
  );
  const snap = await getDocs(q);
  if (!snap.empty) {
    return snap.docs[0].id;
  }
  // Auto-create a new location
  const ref = await addDoc(v3LocationsCol(uid), {
    name: locationName,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
}

// ---------------------------------------------------------------------------
// Games
// ---------------------------------------------------------------------------

/** Build game payload for batch write (caller must provide locationId). */
export function buildGamePayload(
  data: CreateInput<V3Game> & { locationId: string },
): Record<string, unknown> {
  validate(gameCreateSchema, data);
  const pay = { ...data.pay, status: computePayStatus(data.pay) };
  const status = computeGameStatus({
    status: data.status,
    pay,
    dateTime: data.dateTime,
  });
  return stripUndefined({
    ...data,
    pay,
    status,
    createdAt: now(),
    updatedAt: now(),
  } as Record<string, unknown>);
}

export async function createGame(
  uid: string,
  data: CreateInput<V3Game>,
): Promise<string> {
  validate(gameCreateSchema, data);
  const locationId =
    data.locationId ?? (await resolveLocationId(uid, data.locationName));
  const payload = buildGamePayload({ ...data, locationId });
  const ref = await addDoc(v3GamesCol(uid), payload);
  return ref.id;
}

const GAMES_BATCH_SIZE = 250;

export interface CreateGamesBatchOptions {
  onProgress?(done: number, total: number, message?: string): void;
}

/**
 * Build a dedup key for a game. Two games are considered "the same" when their
 * dateTime (to the minute), organizationId, homeTeamName, awayTeamName, and sport
 * all match. Team names are lowercased + trimmed before comparison.
 */
function buildGameDedupKey(input: {
  dateTime: Timestamp;
  organizationId: string;
  sport: string;
  homeTeamName?: string;
  awayTeamName?: string;
}): string {
  const millis = input.dateTime.toMillis();
  const home = (input.homeTeamName ?? "").trim().toLowerCase();
  const away = (input.awayTeamName ?? "").trim().toLowerCase();
  return `${millis}|${input.organizationId}|${input.sport}|${home}|${away}`;
}

/**
 * Create many games in chunked batches. Each item must include locationId.
 *
 * Skips games that match an existing game in the user's collection by
 * (dateTime, organizationId, sport, homeTeamName, awayTeamName). Existing
 * games are fetched once, scoped to the min/max dateTime of the incoming
 * batch (with a one-day pad on each side) to keep the read cheap.
 *
 * Returns created count, per-item errors, and the count/keys of skipped
 * duplicates.
 */
export async function createGamesBatch(
  uid: string,
  games: (CreateInput<V3Game> & { locationId: string })[],
  options?: CreateGamesBatchOptions,
): Promise<{
  created: number;
  errors: Array<{ index: number; message: string }>;
  skippedDuplicates: number;
  skippedKeys: string[];
}> {
  const errors: Array<{ index: number; message: string }> = [];
  const validated: Array<{
    payload: Record<string, unknown>;
    key: string;
    horizonGameId?: string;
  }> = [];
  const total = games.length;

  for (let index = 0; index < games.length; index++) {
    try {
      const g = games[index]!;
      const payload = buildGamePayload(g);
      const key = buildGameDedupKey(g);
      const hid = g.horizonGameId?.trim();
      validated.push({ payload, key, ...(hid ? { horizonGameId: hid } : {}) });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      errors.push({ index, message });
    }
  }

  // Fetch existing games whose dateTime falls within the incoming range
  // (padded by 1 day on each side to tolerate timezone edge cases) and build
  // a Set of dedup keys for fast membership checks.
  const existingKeys = new Set<string>();
  /** Horizon WebRef Game ID# — second dedup axis when present */
  const existingHorizonIds = new Set<string>();
  if (validated.length > 0) {
    let minMillis = Number.POSITIVE_INFINITY;
    let maxMillis = Number.NEGATIVE_INFINITY;
    for (const g of games) {
      const ms = g.dateTime.toMillis();
      if (ms < minMillis) minMillis = ms;
      if (ms > maxMillis) maxMillis = ms;
    }
    const DAY_MS = 24 * 60 * 60 * 1000;
    const minTs = Timestamp.fromMillis(minMillis - DAY_MS);
    const maxTs = Timestamp.fromMillis(maxMillis + DAY_MS);
    try {
      const snap = await getDocs(
        query(
          v3GamesCol(uid),
          where("dateTime", ">=", minTs),
          where("dateTime", "<=", maxTs),
        ),
      );
      snap.docs.forEach((d) => {
        const data = d.data() as Partial<V3Game>;
        const hid = data.horizonGameId?.trim();
        if (hid) existingHorizonIds.add(hid);
        if (!data.dateTime || !data.organizationId || !data.sport) return;
        existingKeys.add(
          buildGameDedupKey({
            dateTime: data.dateTime as Timestamp,
            organizationId: data.organizationId,
            sport: data.sport,
            homeTeamName: data.homeTeamName,
            awayTeamName: data.awayTeamName,
          }),
        );
      });
    } catch (err) {
      console.warn(
        "[createGamesBatch] Existing-games query failed; proceeding without dedup",
        err,
      );
    }
  }

  const skippedKeys: string[] = [];
  const toWrite: Record<string, unknown>[] = [];
  const seenInBatch = new Set<string>();
  const seenHorizonInBatch = new Set<string>();
  for (const item of validated) {
    const hid = item.horizonGameId;
    if (hid) {
      if (existingHorizonIds.has(hid) || seenHorizonInBatch.has(hid)) {
        skippedKeys.push(`hid|${hid}`);
        continue;
      }
      seenHorizonInBatch.add(hid);
    }
    if (existingKeys.has(item.key) || seenInBatch.has(item.key)) {
      skippedKeys.push(item.key);
      continue;
    }
    seenInBatch.add(item.key);
    toWrite.push(item.payload);
  }

  let created = 0;
  for (let i = 0; i < toWrite.length; i += GAMES_BATCH_SIZE) {
    const chunk = toWrite.slice(i, i + GAMES_BATCH_SIZE);
    const batch = writeBatch(getV3Db());
    const col = v3GamesCol(uid);
    for (const payload of chunk) {
      const ref = doc(col);
      batch.set(ref, payload);
      created++;
    }
    await batch.commit();
    options?.onProgress?.(created, total, `Importing games ${created}/${total}…`);
  }

  return {
    created,
    errors,
    skippedDuplicates: skippedKeys.length,
    skippedKeys,
  };
}

export async function updateGame(
  uid: string,
  gameId: string,
  data: UpdateInput<V3Game>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.games, gameId);
  // Recompute pay status then game status if relevant fields changed
  if (data.pay || data.dateTime || data.status) {
    const snap = await getDoc(ref);
    const current = snap.data() as V3Game;
    const pay = data.pay
      ? { ...data.pay, status: computePayStatus(data.pay) }
      : current.pay;
    const merged = {
      status: data.status ?? current.status,
      pay,
      dateTime: data.dateTime ?? current.dateTime,
    };
    data = { ...data, status: computeGameStatus(merged) };
    if (data.pay != null) {
      (data as Record<string, unknown>).pay = pay;
    }
  }
  const payload = stripUndefined({
    ...data,
    updatedAt: now(),
  } as Record<string, unknown>);
  await updateDoc(ref, payload);
}

export async function deleteGame(
  uid: string,
  gameId: string,
): Promise<void> {
  await deleteDoc(v3Doc(uid, V3_COLLECTIONS.games, gameId));
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export async function createPerson(
  uid: string,
  data: CreateInput<V3Person>,
): Promise<string> {
  validate(personCreateSchema, data);
  const ref = await addDoc(
    v3PeopleCol(uid),
    stripUndefined({
      ...data,
      createdAt: now(),
      updatedAt: now(),
    } as Record<string, unknown>),
  );
  return ref.id;
}

export async function updatePerson(
  uid: string,
  personId: string,
  data: UpdateInput<V3Person>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.people, personId);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

export async function deletePerson(
  uid: string,
  personId: string,
): Promise<void> {
  await deleteDoc(v3Doc(uid, V3_COLLECTIONS.people, personId));
}

export interface MergePeopleRefs {
  gameIdsToUpdate: string[];
  orgIdsToUpdateContact: string[];
  teamIdsToUpdateCoach: string[];
}

/**
 * Merge source people into the target person: update target with mergedData,
 * point all games/orgs/teams from source IDs to target, then delete sources.
 * Caller must not pass the current user's person id as a source.
 */
export async function mergePeople(
  uid: string,
  targetPersonId: string,
  sourcePersonIds: string[],
  mergedData: UpdateInput<V3Person>,
  refs: MergePeopleRefs,
): Promise<void> {
  const { gameIdsToUpdate, orgIdsToUpdateContact, teamIdsToUpdateCoach } = refs;
  const sourceSet = new Set(sourcePersonIds);

  await updatePerson(uid, targetPersonId, mergedData);

  for (const gameId of gameIdsToUpdate) {
    const ref = v3Doc(uid, V3_COLLECTIONS.games, gameId);
    const snap = await getDoc(ref);
    const game = snap.data() as V3Game | undefined;
    if (!game?.crew?.length) continue;
    const newCrew: V3CrewMember[] = game.crew.map((m) => {
      if (!sourceSet.has(m.personId)) return m;
      const phone = mergedData.phone ?? m.phone;
      return {
        personId: targetPersonId,
        name: mergedData.name ?? m.name ?? "",
        position: m.position,
        isUser: m.isUser,
        ...(phone != null && phone !== "" ? { phone } : {}),
      };
    });
    await updateGame(uid, gameId, { crew: newCrew });
  }

  for (const orgId of orgIdsToUpdateContact) {
    const ref = v3Doc(uid, V3_COLLECTIONS.organizations, orgId);
    const snap = await getDoc(ref);
    const org = snap.data() as V3Organization | undefined;
    const current = org?.contactPersonIds ?? [];
    const merged = current.map((id) => (sourceSet.has(id) ? targetPersonId : id));
    const deduped = Array.from(new Set(merged));
    await updateOrganization(uid, orgId, { contactPersonIds: deduped });
  }

  for (const teamId of teamIdsToUpdateCoach) {
    await updateTeam(uid, teamId, { coachPersonId: targetPersonId });
  }

  for (const id of sourcePersonIds) {
    await deletePerson(uid, id);
  }
}

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------

export async function createOrganization(
  uid: string,
  data: CreateInput<V3Organization>,
): Promise<string> {
  validate(organizationCreateSchema, data);
  const ref = await addDoc(
    v3OrganizationsCol(uid),
    stripUndefined({
      ...data,
      createdAt: now(),
      updatedAt: now(),
    } as Record<string, unknown>),
  );
  return ref.id;
}

export async function updateOrganization(
  uid: string,
  orgId: string,
  data: UpdateInput<V3Organization>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.organizations, orgId);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

export async function deleteOrganization(
  uid: string,
  orgId: string,
): Promise<void> {
  await deleteDoc(v3Doc(uid, V3_COLLECTIONS.organizations, orgId));
}

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

export async function createTeam(
  uid: string,
  data: CreateInput<V3Team>,
): Promise<string> {
  const ref = await addDoc(
    v3TeamsCol(uid),
    stripUndefined({
      ...data,
      createdAt: now(),
      updatedAt: now(),
    } as Record<string, unknown>),
  );
  return ref.id;
}

export async function updateTeam(
  uid: string,
  teamId: string,
  data: UpdateInput<V3Team>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.teams, teamId);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

export async function deleteTeam(
  uid: string,
  teamId: string,
): Promise<void> {
  await deleteDoc(v3Doc(uid, V3_COLLECTIONS.teams, teamId));
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export async function createLocation(
  uid: string,
  data: CreateInput<V3Location>,
): Promise<string> {
  validate(locationCreateSchema, data);
  const ref = await addDoc(
    v3LocationsCol(uid),
    stripUndefined({
      ...data,
      createdAt: now(),
      updatedAt: now(),
    } as Record<string, unknown>),
  );
  return ref.id;
}

export async function updateLocation(
  uid: string,
  locationId: string,
  data: UpdateInput<V3Location>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.locations, locationId);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

export async function deleteLocation(
  uid: string,
  locationId: string,
): Promise<void> {
  await deleteDoc(v3Doc(uid, V3_COLLECTIONS.locations, locationId));
}

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

export async function createExpense(
  uid: string,
  data: CreateInput<V3Expense>,
): Promise<string> {
  validate(expenseCreateSchema, data);
  const payload = stripUndefined({ ...data, createdAt: now(), updatedAt: now() } as Record<string, unknown>);
  const ref = await addDoc(v3ExpensesCol(uid), payload);
  return ref.id;
}

export async function updateExpense(
  uid: string,
  expenseId: string,
  data: UpdateInput<V3Expense>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.expenses, expenseId);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

export async function deleteExpense(
  uid: string,
  expenseId: string,
): Promise<void> {
  await deleteDoc(v3Doc(uid, V3_COLLECTIONS.expenses, expenseId));
}

// ---------------------------------------------------------------------------
// Mileage Trips
//
// @deprecated The `mileageTrips` subcollection is not written from any UI path.
// Canonical mileage lives on `games/{id}.miles` + `games/{id}.mileageReconciledAt`
// (see `src/app/finances/mileage/page.tsx`). These writes remain so any
// imported / server-seeded trips can be migrated forward in a later pass.
// See `design/finance-lifecycle-review.md` §5 Phase 1c.
// ---------------------------------------------------------------------------

/** @deprecated See comment block above; prefer `updateGame` with `mileage.*`. */
export async function createMileageTrip(
  uid: string,
  data: CreateInput<V3MileageTrip>,
): Promise<string> {
  // Ensure round-trip mileage: deductible = oneWay * 2
  const milesRoundTrip = data.milesOneWay * 2;
  const milesDeductible = milesRoundTrip;
  const payload = stripUndefined({
    ...data,
    milesRoundTrip,
    milesDeductible,
    createdAt: now(),
    updatedAt: now(),
  } as Record<string, unknown>);
  const ref = await addDoc(v3MileageTripsCol(uid), payload);
  return ref.id;
}

/** @deprecated See note on `createMileageTrip`. */
export async function updateMileageTrip(
  uid: string,
  tripId: string,
  data: UpdateInput<V3MileageTrip>,
): Promise<void> {
  // Recompute round-trip mileage if oneWay changed
  if (data.milesOneWay != null) {
    data = {
      ...data,
      milesRoundTrip: data.milesOneWay * 2,
      milesDeductible: data.milesOneWay * 2,
    };
  }
  const ref = v3Doc(uid, V3_COLLECTIONS.mileageTrips, tripId);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

/** @deprecated See note on `createMileageTrip`. */
export async function deleteMileageTrip(
  uid: string,
  tripId: string,
): Promise<void> {
  await deleteDoc(v3Doc(uid, V3_COLLECTIONS.mileageTrips, tripId));
}

// ---------------------------------------------------------------------------
// Tax Config
// ---------------------------------------------------------------------------

export async function setTaxYearConfig(
  uid: string,
  year: string,
  data: Omit<V3TaxYearConfig, "createdAt" | "updatedAt">,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.taxConfig, year);
  await setDoc(ref, { ...data, createdAt: now(), updatedAt: now() }, { merge: true });
}

export async function updateTaxYearConfig(
  uid: string,
  year: string,
  data: Partial<V3TaxYearConfig>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.taxConfig, year);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

// ---------------------------------------------------------------------------
// Voice Notes
// ---------------------------------------------------------------------------

export async function createVoiceNote(
  uid: string,
  data: CreateInput<V3VoiceNote>,
): Promise<string> {
  const ref = await addDoc(v3VoiceNotesCol(uid), {
    ...data,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
}

export async function updateVoiceNote(
  uid: string,
  noteId: string,
  data: UpdateInput<V3VoiceNote>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.voiceNotes, noteId);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

export async function deleteVoiceNote(
  uid: string,
  noteId: string,
): Promise<void> {
  await deleteDoc(v3Doc(uid, V3_COLLECTIONS.voiceNotes, noteId));
}

// ---------------------------------------------------------------------------
// Import Jobs
// ---------------------------------------------------------------------------

export async function createImportJob(
  uid: string,
  data: CreateInput<V3ImportJob>,
): Promise<string> {
  const ref = await addDoc(v3ImportJobsCol(uid), {
    ...data,
    createdAt: now(),
    updatedAt: now(),
  });
  return ref.id;
}

export async function updateImportJob(
  uid: string,
  jobId: string,
  data: UpdateInput<V3ImportJob>,
): Promise<void> {
  const ref = v3Doc(uid, V3_COLLECTIONS.importJobs, jobId);
  await updateDoc(ref, stripUndefined({ ...data, updatedAt: now() } as Record<string, unknown>));
}

// ---------------------------------------------------------------------------
// Export Records
// ---------------------------------------------------------------------------

export async function createExportRecord(
  uid: string,
  data: Omit<V3ExportRecord, "id" | "createdAt">,
): Promise<string> {
  const ref = await addDoc(v3ExportsCol(uid), {
    ...data,
    createdAt: now(),
  });
  return ref.id;
}

// ---------------------------------------------------------------------------
// User Profile
// ---------------------------------------------------------------------------

export async function setUserProfile(
  uid: string,
  data: Omit<V3UserProfile, "createdAt">,
): Promise<void> {
  const ref = v3UserDoc(uid);
  await setDoc(ref, { ...data, createdAt: now() }, { merge: true });
}

export async function updateUserProfile(
  uid: string,
  data: Partial<V3UserProfile>,
): Promise<void> {
  const ref = v3UserDoc(uid);
  await updateDoc(ref, stripUndefined(data as Record<string, unknown>));
}

/** Merge partial fields into user profile (creates doc if missing). */
export async function setUserProfileMerge(
  uid: string,
  data: Partial<V3UserProfile>,
): Promise<void> {
  const ref = v3UserDoc(uid);
  await setDoc(ref, stripUndefined(data as Record<string, unknown>), { merge: true });
}

// ---------------------------------------------------------------------------
// Settings (key-value docs: gameDefaults, calendarSync, debug, subscription, etc.)
// ---------------------------------------------------------------------------

export async function setSetting(
  uid: string,
  key: string,
  data: Record<string, unknown>,
  merge = true,
): Promise<void> {
  const ref = v3SettingDoc(uid, key);
  await setDoc(ref, stripUndefined(data), { merge });
}

// ---------------------------------------------------------------------------
// Addresses (user saved addresses)
// ---------------------------------------------------------------------------

export async function setAddressDoc(
  uid: string,
  docId: string,
  data: Record<string, unknown>,
  merge = true,
): Promise<void> {
  const ref = doc(v3AddressesCol(uid), docId);
  await setDoc(ref, stripUndefined(data), { merge });
}

export async function addAddress(
  uid: string,
  data: Record<string, unknown>,
): Promise<string> {
  const ref = await addDoc(v3AddressesCol(uid), stripUndefined(data));
  return ref.id;
}

export async function deleteAddress(uid: string, docId: string): Promise<void> {
  const ref = doc(v3AddressesCol(uid), docId);
  await deleteDoc(ref);
}

// ---------------------------------------------------------------------------
// Batch Operations
// ---------------------------------------------------------------------------

export function createV3Batch() {
  return writeBatch(getV3Db());
}

// ---------------------------------------------------------------------------
// Auto-Link Game Locations
// ---------------------------------------------------------------------------

export async function autoLinkGameLocations(
  uid: string,
  games: Array<{ id: string; location: string; locationId?: string }>,
  locations: Array<{ id: string; name: string }>,
): Promise<{
  linked: number;
  nameSynced: number;
  unresolvable: string[];
  /** Game documents updated in this run (link or name sync). */
  updatedGameIds: string[];
}> {
  const locByNameLower = new Map<string, Array<{ id: string; name: string }>>();
  const locIdSet = new Set(locations.map((l) => l.id));
  const locById = new Map(locations.map((l) => [l.id, l]));

  for (const loc of locations) {
    const key = loc.name.toLowerCase().trim();
    if (!key) continue;
    const arr = locByNameLower.get(key) || [];
    arr.push(loc);
    locByNameLower.set(key, arr);
  }

  const batch = writeBatch(getV3Db());
  let linked = 0;
  let nameSynced = 0;
  const unresolvable: string[] = [];
  const updatedGameIds: string[] = [];
  let batchCount = 0;

  for (const game of games) {
    const gLocId = game.locationId || "";
    const gLocName = game.location || "";
    const nameKey = gLocName.toLowerCase().trim();
    const nameMatches = nameKey ? locByNameLower.get(nameKey) : undefined;
    const uniqueMatch = nameMatches && nameMatches.length === 1 ? nameMatches[0] : null;
    const gameRef = doc(v3GamesCol(uid), game.id);

    if (!gLocId && gLocName) {
      // Missing locationId -- link by name match
      if (uniqueMatch) {
        batch.update(gameRef, { locationId: uniqueMatch.id, updatedAt: now() });
        batchCount++;
        linked++;
        updatedGameIds.push(game.id);
      } else {
        unresolvable.push(`${game.id}: "${gLocName}"`);
      }
    } else if (gLocId && !locIdSet.has(gLocId)) {
      // Orphan locationId -- re-link by name match
      if (uniqueMatch) {
        batch.update(gameRef, { locationId: uniqueMatch.id, updatedAt: now() });
        batchCount++;
        linked++;
        updatedGameIds.push(game.id);
      } else {
        unresolvable.push(`${game.id}: "${gLocName}"`);
      }
    } else if (gLocId && locIdSet.has(gLocId)) {
      // Valid ID but check for name mismatch
      const loc = locById.get(gLocId);
      if (loc && gLocName && loc.name && gLocName.toLowerCase().trim() !== loc.name.toLowerCase().trim()) {
        batch.update(gameRef, { locationName: loc.name, updatedAt: now() });
        batchCount++;
        nameSynced++;
        updatedGameIds.push(game.id);
      }
    }

    if (batchCount >= 450) {
      await batch.commit();
      batchCount = 0;
    }
  }

  if (batchCount > 0) {
    await batch.commit();
  }

  return { linked, nameSynced, unresolvable, updatedGameIds };
}

export interface VenueGroupForAutoCreate {
  displayName: string;
  gameIds: string[];
}

/**
 * Create a saved location per venue group, geocode within home-base radius when possible,
 * compute distance from home, then set locationId on each game.
 */
export async function autoCreateLocationsAndLinkGames(opts: {
  uid: string;
  homeAddress: string | null;
  venueGroups: VenueGroupForAutoCreate[];
}): Promise<{
  locationsCreated: number;
  geocodedOk: number;
  stubNoAddress: number;
  gamesLinked: number;
  createdLocations: Array<{ id: string; name: string }>;
  errors: string[];
}> {
  const { uid, homeAddress, venueGroups } = opts;
  const searchContext = (homeAddress ?? "").trim();
  const { findLocationAddress } = await import("@/app/actions");
  const { calculateMileage } = await import("@/app/actions");
  const { getLocationSearchVariations } = await import("@/lib/location-abbreviation-expander");
  const { propagateLocationDistanceToGames } = await import("@/lib/mileage-calculator");

  let locationsCreated = 0;
  let geocodedOk = 0;
  let stubNoAddress = 0;
  let gamesLinked = 0;
  const createdLocations: Array<{ id: string; name: string }> = [];
  const errors: string[] = [];

  for (const group of venueGroups) {
    const displayName = group.displayName.trim();
    if (!displayName || group.gameIds.length === 0) continue;

    try {
      const locationId = await createLocation(uid, { name: displayName });
      locationsCreated++;
      createdLocations.push({ id: locationId, name: displayName });

      let resolvedAddress: string | null = null;
      let coords: { lat: number; lng: number } | null = null;

      for (const searchName of getLocationSearchVariations(displayName)) {
        const result = await findLocationAddress({
          locationName: searchName,
          searchContext,
        });
        if (result.warning) {
          continue;
        }
        if (result.address) {
          resolvedAddress = result.address;
          coords = result.location ?? null;
          geocodedOk++;
          break;
        }
      }

      if (resolvedAddress) {
        await updateLocation(
          uid,
          locationId,
          stripUndefined({
            address: resolvedAddress,
            coordinates: coords ? { lat: coords.lat, lng: coords.lng } : undefined,
          } as Record<string, unknown>),
        );

        if (searchContext) {
          try {
            const mileageResult = await calculateMileage({
              startAddress: searchContext,
              endAddress: resolvedAddress,
              tripType: "one-way",
            });
            const distanceMiles = Math.round(mileageResult.miles);
            const travelTimeMin = Math.max(0, Math.round(mileageResult.travelTime ?? 0));
            await updateLocation(uid, locationId, {
              distanceFromHome: distanceMiles,
              travelTimeFromHome: travelTimeMin,
            });
            await propagateLocationDistanceToGames(
              uid,
              locationId,
              distanceMiles,
              travelTimeMin,
              resolvedAddress,
              searchContext,
            );
          } catch (e) {
            console.error("[autoCreateLocationsAndLinkGames] mileage failed", e);
            errors.push(
              `${displayName}: mileage — ${e instanceof Error ? e.message : String(e)}`,
            );
          }
        }
      } else {
        stubNoAddress++;
      }

      const col = v3GamesCol(uid);
      for (let i = 0; i < group.gameIds.length; i += 450) {
        const chunk = group.gameIds.slice(i, i + 450);
        const batch = writeBatch(getV3Db());
        for (const gameId of chunk) {
          batch.update(doc(col, gameId), {
            locationId,
            updatedAt: now(),
          });
        }
        await batch.commit();
        gamesLinked += chunk.length;
      }
    } catch (e) {
      errors.push(`${displayName}: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return {
    locationsCreated,
    geocodedOk,
    stubNoAddress,
    gamesLinked,
    createdLocations,
    errors,
  };
}
